"use client";

import { useCallback, useId, useRef, useState } from "react";

import { releaseCardImageStackModel } from "@/components/release-card-image-stack-model";
import { ReleaseCardImageStackView } from "@/components/release-card-image-stack-view";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useInView } from "@/hooks/use-in-view";
import { useReleaseImagesResource } from "@/hooks/use-release-images-resource";

interface ReleaseCardImageStackProps {
  className?: string;
  imageCount?: number | null;
  releaseId: string;
  title: string;
}

const RELEASE_IMAGES_MODAL = "release-images";

interface ReleaseImageZoomPayload {
  index: number;
}

function restoreGalleryFocus(ownerId: string) {
  window.setTimeout(() => {
    window.requestAnimationFrame(() => {
      const triggers = document.querySelectorAll<HTMLButtonElement>(
        "button[data-release-gallery-owner]",
      );
      Array.from(triggers)
        .find((trigger) => trigger.dataset.releaseGalleryOwner === ownerId)
        ?.focus();
    });
  }, 100);
}

export function ReleaseCardImageStack({
  className,
  imageCount,
  releaseId,
  title,
}: ReleaseCardImageStackProps) {
  const galleryId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const previewTriggerRef = useRef<HTMLButtonElement>(null);
  const inView = useInView(containerRef, { rootMargin: "300px" });
  const hashModal = useHashModal();
  const modalOwnerId = `release-card-gallery:${releaseId}:${galleryId}`;
  const lightboxState = hashModal.getState<ReleaseImageZoomPayload>(RELEASE_IMAGES_MODAL, {
    ownerId: modalOwnerId,
  });
  const lightboxIndex = lightboxState.open ? lightboxState.payload.index : null;
  const { images } = useReleaseImagesResource(releaseId, {
    enabled: (imageCount == null && inView) || lightboxIndex !== null,
  });
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const model = releaseCardImageStackModel({
    focusWithin,
    hovered,
    imageCount,
    loadedImageCount: images.length,
  });

  const closeLightbox = useCallback(() => {
    restoreGalleryFocus(modalOwnerId);
    hashModal.close(RELEASE_IMAGES_MODAL, { ownerId: modalOwnerId });
  }, [hashModal, modalOwnerId]);

  return (
    <ReleaseCardImageStackView
      className={className}
      closeLightbox={closeLightbox}
      containerRef={containerRef}
      images={images}
      lightboxIndex={lightboxIndex}
      onFocusWithinChange={setFocusWithin}
      onHoveredChange={setHovered}
      onOpen={(index) => {
        hashModal.open<ReleaseImageZoomPayload>(
          RELEASE_IMAGES_MODAL,
          { index },
          { ownerId: modalOwnerId },
        );
      }}
      previewTriggerOwnerId={modalOwnerId}
      previewTriggerRef={previewTriggerRef}
      releaseId={releaseId}
      title={title}
      {...model}
    />
  );
}
